import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question:
      "What are the fastest treatment options for treatment-resistant depression?",
    answer:
      "Options include medication adjustments, psychotherapy, traditional TMS, accelerated TMS (SAINT®), Spravato (esketamine), and ketamine. 'Fast' varies — SAINT® is delivered in five days, Spravato may produce effects within hours, but individual response varies. No option is universally 'best.' A psychiatric evaluation determines which may be appropriate for you.",
  },
  {
    question: "Is faster treatment always better?",
    answer:
      "No. Speed of delivery is one factor. Effectiveness, safety, cost, insurance coverage, and individual suitability matter too. A faster treatment that is not appropriate for your situation is not better. Discuss the full range of options with your psychiatrist before deciding.",
  },
  {
    question: "How does SAINT® compare to Spravato for speed?",
    answer:
      "SAINT® is delivered over five days; Spravato is administered twice a week for four weeks, then weekly. Spravato may produce effects within hours of a session. SAINT®'s remission was measured at four weeks in trials. They work differently — SAINT® is brain stimulation, Spravato is a medication. Individual response varies for both.",
  },
  {
    question: "Can medication changes work quickly?",
    answer:
      "Sometimes. Adjusting a dose, switching medications, or adding an augmenting agent may produce improvement, but antidepressants typically take four to eight weeks to show full effect. Medication changes are often the first step, but if multiple trials have failed, other options may be considered.",
  },
  {
    question: "What should I discuss with my psychiatrist?",
    answer:
      "Discuss your full treatment history, what has and has not worked, how quickly you need relief, what your insurance covers, and what the risks and benefits of each option are. A psychiatric evaluation can clarify which treatment may be appropriate for your specific situation.",
  },
];

export const pageFastTrd: LearnPageData = {
  slug: "fast-treatment-resistant-depression-options",
  title: "Faster Treatment for Treatment-Resistant Depression | Neuro Wellness",
  description:
    "Faster treatment options for treatment-resistant depression: medication, therapy, TMS, SAINT®, Spravato, ketamine. Why fast does not mean best for everyone. Consultation next step.",
  canonical: "/learn/fast-treatment-resistant-depression-options",
  h1: "Faster Treatment Options for Treatment-Resistant Depression",
  breadcrumbLabel: "Fast TRD Treatment Options",
  crumbs: baseCrumbs("Fast TRD Treatment Options"),
  shortAnswer:
    "Treatment-resistant depression — major depressive disorder that has not improved adequately after at least two antidepressant trials — can be frustrating, and patients often want faster relief. Options include medication adjustments, psychotherapy, traditional TMS, accelerated TMS (SAINT®), Spravato (esketamine), and ketamine. 'Fast' varies: SAINT® is delivered in five days, Spravato may produce effects within hours, but individual response varies. No single option is universally 'best' — faster does not necessarily mean better for everyone. This page compares the options and explains why a psychiatric consultation is the right next step.",
  keyTakeaways: [
    "Multiple options exist for treatment-resistant depression.",
    "SAINT® is delivered in five days; Spravato may act within hours.",
    "Speed is one factor — effectiveness and safety matter too.",
    "Faster does not mean better for everyone.",
    "A psychiatric consultation determines what may be appropriate.",
  ],

  sections: [
    {
      heading: "Medication Changes",
      body: (
        <>
          {P(
            "Medication adjustments — optimizing dose, switching to a different antidepressant, or adding an augmenting agent — are often the first step. Antidepressants typically take four to eight weeks to show full effect. If multiple trials have failed, other options may be considered. Do not stop medication on your own; discuss changes with your prescriber.",
          )}
          {P(
            "Learn more about what to try when antidepressants don\u2019t work.",
          )}
        </>
      ),
    },
    {
      heading: "Psychotherapy",
      body: (
        <>
          {P(
            "Psychotherapy — including CBT, behavioral activation, and interpersonal therapy — is effective for depression and may be combined with medication or brain stimulation. Therapy is not 'fast' in the sense of producing immediate relief, but it addresses underlying patterns and skills. It is often part of a comprehensive plan.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/services/psychotherapy"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                psychotherapy
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Traditional TMS",
      body: (
        <>
          {P(
            "Traditional TMS is FDA-cleared for adults with major depressive disorder who have not improved on antidepressants. It is delivered one session per day, five days a week, for about six weeks. It is broadly covered by insurance. It is not 'fast' in terms of delivery, but it is non-invasive and medication-free.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-tms-therapy-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                TMS therapy
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Accelerated TMS (SAINT®)",
      body: (
        <>
          {P(
            "SAINT® is an FDA-cleared accelerated TMS protocol delivered over five consecutive days with fMRI-guided targeting. It is faster in terms of delivery than traditional TMS. In trials, remission was measured at four weeks. SAINT® is typically self-pay. Individual response varies.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/what-is-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT® TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Spravato (Esketamine)",
      body: (
        <>
          {P(
            "Spravato is an FDA-approved nasal spray for treatment-resistant depression. It is administered in a certified clinic twice a week for four weeks, then weekly. It may produce effects within hours of a session. It causes dissociation and requires two hours of monitoring. Coverage varies by plan.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/spravato-vs-tms-for-depression"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                Spravato vs TMS
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Ketamine",
      body: (
        <>
          {P(
            "IV ketamine is used off-label for depression. It is administered by infusion in a clinic and may produce rapid effects. It is off-label because ketamine is FDA-approved only as an anesthetic. It causes dissociation and requires monitoring. Insurance is less likely to cover it than Spravato.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/saint-vs-ketamine"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT® vs ketamine
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Why 'Fast' Does Not Mean 'Best for Everyone'",
      body: (
        <>
          {P(
            "Speed of delivery is one factor. Effectiveness, safety, cost, insurance coverage, and individual suitability matter too. A faster treatment that is not appropriate for your situation is not better. Some patients respond well to medication changes; others need brain stimulation. A psychiatric evaluation can clarify which option may be appropriate for you.",
          )}
        </>
      ),
    },
    {
      heading: "Consultation as the Next Step",
      body: (
        <>
          {P(
            "The right next step is a psychiatric consultation. Discuss your full treatment history, what has and has not worked, how quickly you need relief, what your insurance covers, and the risks and benefits of each option. Neuro Wellness offers consultations — in person or by telehealth.",
          )}
          {P(
            <>
              To request a consultation,{" "}
              <a
                href="/contactus"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                contact our team
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
    {
      citation:
        "U.S. Food and Drug Administration. Spravato (esketamine) prescribing information.",
      url: "https://www.accessdata.fda.gov/drugsatfda_docs/label/2025/211243s019lbl.pdf",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/fast-treatment-resistant-depression-options#webpage`,
        name: "Faster Treatment Options for Treatment-Resistant Depression",
        url: `${SITE_URL}/learn/fast-treatment-resistant-depression-options`,
        description:
          "Faster TRD treatment options: medication, therapy, TMS, SAINT®, Spravato, ketamine. Why fast does not mean best for everyone.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/learn/fast-treatment-resistant-depression-options#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("Fast TRD Treatment Options")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "When Antidepressants Don't Work",
      href: "/learn/when-antidepressants-dont-work",
      description: "What to try next",
    },
    {
      title: "SAINT® vs Spravato",
      href: "/learn/saint-vs-spravato",
      description: "Comparing two options",
    },
  ],
};
